/**
 * The single open gate, answered through the canonical DecisionCard (R2), in
 * the case file's footer grammar (simplification, 2026-09-24):
 *
 *   LABEL   one consequence sentence   [secondary verb] [PRIMARY]
 *
 * The label names the ask (Accept proposal, Approve plan, Stopped to ask), the
 * sentence says what the primary does, and the primary is always rightmost.
 * There is no "Not now": an ask whose only other move is to leave it waiting
 * has no secondary at all. Once acted on, the footer prints the stamp, what
 * happened, and the page's own moves on (Archive, Next item).
 *
 * The gate's editable proposal, when its payload carries one, is the artifact
 * above the verbs, and an APPROVE carries `editedProposal` to the resolve
 * endpoint, the same body the Feed sends, so an edit made here is captured.
 *
 * A plan gate is not answered here: the ticket sheet holds the plan's
 * questions, its versions and Approve (spec 2026-11-02). This card is mounted
 * for review cases (`ReviewCaseFile`), and a plan gate that reaches it reads
 * as an ask with no verbs.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type {
  EditableProposal,
  JobAuthority,
  JobGatePayload,
  RepoSuggestion,
  WorkItemGate,
} from "@yagni/shared";
import {
  acceptProposalEffect,
  acceptProposalHandedOff,
  GATE_NOT_A_MEMBER,
  gatePrimaryBlock,
  isClosedPullRequestStop,
  isPlanAsk,
  JOB_ACT_COPY,
  jobActCappedCopy,
  jobActVariantCopy,
  jobActElsewhere,
  readJobGatePayload,
  resumesApprovedPlan,
  supervisedByLine,
  whoseClickLine,
  type JobActChoiceId,
} from "@yagni/shared";
import { useResolveWorkItemGate, type ResolveGateResponse } from "../tracker/useWorkItemGate.js";
import {
  REPO_CHOOSE_TO_START,
  REPO_LABEL,
  RepoSelect,
  patchWorkItemRepo,
  readRepoList,
  refusalCode,
  type TeamRepo,
} from "./repoPick.js";
import { DecisionCard, type DecisionIntent } from "../decisions/DecisionCard.js";
import {
  COMMISSION_PROJECT_CONSEQUENCE,
  COMMISSION_PROJECT_RECEIPT,
  COMMISSION_PROJECT_VERB,
  HANDOFF_FLIP_MS,
  MILESTONE_DECOMPOSITION_GATE_KIND,
  MILESTONE_PUSH_CONSEQUENCE,
  MILESTONE_PUSH_RECEIPT,
  MILESTONE_PUSH_VERB,
} from "../decisions/consequence.js";
import { useCommissionProject, usePushMilestone } from "../tracker/useCommissionProject.js";
import { PatchRoundEvidence } from "./PatchRoundEvidence.js";
import { ReviewFindingsCards, type ReviewFindingsState } from "./ReviewFindings.js";
import { FootLead, StampRow } from "./CaseDock.js";
import { AskFacts, AskNeed, gateAskOf } from "./AskFacts.js";
import {
  ACCEPT_PROPOSAL_EFFECT,
  ACCEPT_PROPOSAL_VERB,
  DELIVERY_DONE_EFFECT,
  DELIVERY_DONE_HANDED_OFF,
  DELIVERY_DONE_STAMP,
  DELIVERY_DONE_VERB,
  GATE_LABEL,
  GATE_STAMPS,
  REQUEST_CHANGES_PLACEHOLDER,
  REQUEST_CHANGES_VERB,
  REVIEW_CONFIRM_EFFECT,
  REVIEW_CONFIRM_HANDED_OFF,
  REVIEW_CONFIRM_NOTHING_KEPT,
  REVIEW_CONFIRM_KEPT_DRAFT,
  REVIEW_CONFIRM_NOT_POSTED_FAILED,
  REVIEW_CONFIRM_NOT_POSTED_STALE,
  REVIEW_CONFIRM_VERB,
  CLOSED_PR_CLOSE_EFFECT,
  CLOSED_PR_CLOSE_VERB,
  CLOSED_PR_RUN_AGAIN_EFFECT,
  PLAIN_ASK_DISMISS,
  PLAIN_ASK_DISMISS_EFFECT,
  PLAIN_ASK_EFFECT,
  PLAIN_ASK_SEND,
  gateRecordNote,
  plainAskReceipt,
  stampByLine,
} from "./workCopy.js";

/** The gate's editable proposal, when the open-gate payload carries one (else null). */
function gateProposal(gate: WorkItemGate): EditableProposal | null {
  const p = gate.payload?.proposal;
  if (
    p &&
    typeof p === "object" &&
    !Array.isArray(p) &&
    Array.isArray((p as { fields?: unknown }).fields)
  ) {
    return p as unknown as EditableProposal;
  }
  return null;
}

interface GateVerbConfig {
  /** The footer's label: what is being asked. */
  label: string;
  verbLabel: string;
  /** The one consequence sentence: what the primary does. */
  effect: string;
  /** What the secondary does, when the secondary is itself an act. */
  declineEffect?: string;
  /** The secondary verb; null when the ask has none. */
  declineLabel: string | null;
  /** The prose field: the answer for needs_input, the note for a send-back. */
  feedback: { placeholder: string } | null;
  /** The handed-off line the footer prints after the primary lands. */
  handedOff: string;
  /** The third ending, on the one card that has one (a fix round out of rounds). */
  alternative?: {
    choice: JobActChoiceId;
    label: string;
    effect: string;
    handedOff: string;
    stamp: string;
  };
  approveChoice?: JobActChoiceId;
  declineChoice?: JobActChoiceId;
  approveStamp?: string;
  declineStamp?: string;
  declineHandedOff?: string;
}

/** A held PR-review gate (YAG-348): its payload routes a review post, not a lifecycle step. */
export function isHeldReviewGate(gate: WorkItemGate): boolean {
  const payload = gate.payload;
  return Boolean(
    payload &&
      typeof payload === "object" &&
      (payload as Record<string, unknown>).heldReviewPayload,
  );
}

/** The label a Job's act reads as in the footer. */
function actLabel(act: string): string {
  if (act === "accept_proposal") return GATE_LABEL.proposal;
  if (act === "proceed_to_build") return GATE_LABEL.plan;
  if (act === "push_fix") return GATE_LABEL.fix;
  if (act === "merge") return GATE_LABEL.merge;
  return GATE_LABEL.other;
}

/**
 * The copy for one gate, read off the ACT the gate stages (ADR-0044). A gate
 * minted by a Job carries `{job, act, evidence, target}`, and the act is what
 * a person is being asked; the gate KIND is only where the row lives.
 */
function jobActConfig(read: JobGatePayload): GateVerbConfig | null {
  // A plan is answered on the ticket sheet, never on this card.
  if (!read.act || read.act === "proceed_to_build") return null;
  const copy = JOB_ACT_COPY[read.act];
  if (!copy) return null;
  // Out of rounds wins; otherwise a decision the evidence names (a built fix
  // round waiting to be pushed, a round that stopped) has its own three verbs.
  const capped = read.cappedByPerItemCap
    ? jobActCappedCopy(read.act, read.job?.agentName)
    : jobActVariantCopy(read.act, read.evidence.kind, read.job?.agentName, read.evidence.askKind);
  if (capped) {
    return {
      label: GATE_LABEL.fix,
      verbLabel: capped.verb,
      effect: capped.effect,
      handedOff: capped.handedOff,
      approveChoice: "continue",
      approveStamp: capped.stamp,
      alternative: capped.alternative,
      declineLabel: capped.declineLabel,
      declineEffect: capped.declineEffect,
      declineChoice: capped.declineChoice,
      declineStamp: capped.declineStamp,
      declineHandedOff: capped.declineHandedOff,
      feedback: { placeholder: capped.feedbackPlaceholder },
    };
  }
  return {
    label: actLabel(read.act),
    verbLabel: copy.verb,
    effect: copy.effect,
    ...(copy.declineEffect ? { declineEffect: copy.declineEffect } : {}),
    declineLabel: copy.declineLabel,
    feedback: copy.feedbackPlaceholder ? { placeholder: copy.feedbackPlaceholder } : null,
    handedOff: copy.handedOff,
  };
}

/**
 * The copy per gate kind, for a gate no Job minted. `null` is a gate this
 * footer has no verbs for (verify_receipt resolves elsewhere).
 */
function gateVerbConfig(
  kind: WorkItemGate["kind"],
  heldReview: boolean,
  resumesPlan: boolean,
  closedPullRequest: boolean,
): GateVerbConfig | null {
  // The held review draft: confirming posts to GitHub. Keep, edit and remove
  // ARE the changes, so there is no second verb (the backend refuses one).
  if (kind === "review_result" && heldReview) {
    return {
      label: GATE_LABEL.review,
      verbLabel: REVIEW_CONFIRM_VERB,
      effect: REVIEW_CONFIRM_EFFECT,
      declineLabel: null,
      feedback: null,
      handedOff: REVIEW_CONFIRM_HANDED_OFF,
    };
  }
  switch (kind) {
    case "accept_proposal":
      // "Start plan": accepting lands the accepted phase with `due_at = now()`
      // in one statement, so the verb names what happens.
      return {
        label: GATE_LABEL.proposal,
        verbLabel: ACCEPT_PROPOSAL_VERB,
        effect: ACCEPT_PROPOSAL_EFFECT,
        declineLabel: "Doesn't need work",
        feedback: null,
        handedOff: PROPOSAL_HANDED_OFF,
      };
    case "needs_input":
      // The resumable stop card: the plan is intact and answering runs it
      // again, so prose is optional context. A question-shaped ask needs its
      // answer. Leaving either waiting is not a verb.
      if (closedPullRequest) {
        // The pull request closed unmerged: building again opens a new one,
        // and closing the ticket is a real ending too.
        return {
          label: GATE_LABEL.stopped,
          verbLabel: "Run it again",
          effect: CLOSED_PR_RUN_AGAIN_EFFECT,
          declineLabel: CLOSED_PR_CLOSE_VERB,
          declineEffect: CLOSED_PR_CLOSE_EFFECT,
          feedback: { placeholder: "Optional note for the restart…" },
          handedOff: "Plan re-offered. Approve it to run again.",
        };
      }
      if (resumesPlan) {
        return {
          label: GATE_LABEL.stopped,
          verbLabel: "Run it again",
          effect: "The approved plan is unchanged. Running it again builds it now.",
          declineLabel: null,
          feedback: { placeholder: "Optional note for the restart…" },
          handedOff: "Plan re-offered. Approve it to run again.",
        };
      }
      // A plain question: answer it, or say it is not needed. The receipt
      // is read off the response, never promised here.
      return {
        label: GATE_LABEL.stopped,
        verbLabel: PLAIN_ASK_SEND,
        effect: PLAIN_ASK_EFFECT,
        declineLabel: PLAIN_ASK_DISMISS,
        declineEffect: PLAIN_ASK_DISMISS_EFFECT,
        feedback: { placeholder: "Your answer…" },
        handedOff: plainAskReceipt(null),
      };
    case "review_result":
      // A delivery with no pull request (a document, a CLI handoff): the one
      // result a person still marks done by hand. A ticket with a pull request
      // mints no such gate; it reaches Done when the pull request merges.
      return {
        label: GATE_LABEL.delivery,
        verbLabel: DELIVERY_DONE_VERB,
        effect: DELIVERY_DONE_EFFECT,
        declineLabel: REQUEST_CHANGES_VERB,
        feedback: { placeholder: REQUEST_CHANGES_PLACEHOLDER },
        handedOff: DELIVERY_DONE_HANDED_OFF,
      };
    case "commission_project":
      return {
        label: GATE_LABEL.other,
        verbLabel: COMMISSION_PROJECT_VERB,
        effect: COMMISSION_PROJECT_CONSEQUENCE,
        declineLabel: null,
        feedback: null,
        handedOff: COMMISSION_PROJECT_RECEIPT,
      };
    case MILESTONE_DECOMPOSITION_GATE_KIND:
      // The second confirm: these tickets, this milestone, out to the tracker.
      // Its one verb is the push; leaving it waiting is not a verb.
      return {
        label: GATE_LABEL.other,
        verbLabel: MILESTONE_PUSH_VERB,
        effect: MILESTONE_PUSH_CONSEQUENCE,
        declineLabel: null,
        feedback: null,
        handedOff: MILESTONE_PUSH_RECEIPT,
      };
    default:
      return null;
  }
}

/** The handed-off state of an accepted proposal (gate or gate-less accept). */
export const PROPOSAL_HANDED_OFF = acceptProposalHandedOff(null);

export function proposalHandoffCopy(
  planning: ResolveGateResponse["planning"],
  planAuthority?: JobAuthority | null,
): string {
  if (planning?.status !== "not_started") return acceptProposalHandedOff(planAuthority);
  return ["Accepted. Planning has not started.", planning.reason].filter(Boolean).join(" ");
}

/** What the footer shows once the gate is answered, for the page to keep showing. */
export interface GateSettled {
  stamp: string;
  text: string;
}

export function GateBlock({
  gate,
  review,
  workerName,
  resolverName,
  onResolved,
  afterActs,
  footMore,
  isTeamMember,
  repoSuggestion,
  planAuthority = null,
}: {
  /**
   * The rung of the Team's Plan line, when the host knows it: Start plan's
   * sentence says an Autonomous line approves its own plan. Null keeps the
   * sign-off words.
   */
  planAuthority?: JobAuthority | null;
  gate: WorkItemGate;
  /** The deep-review state, when this gate is a review hold with drafted findings. */
  review?: ReviewFindingsState;
  /** The worker whose work this is, for the record note. Null keeps it plain. */
  workerName?: string | null;
  /** The signed-in reader's name, for the stamp they are about to earn. */
  resolverName?: string | null;
  /**
   * The gate is answered. An approval that STARTED a run says which one, and
   * every answer says what the footer printed, so the page can keep the stamp
   * after the gate itself is gone.
   */
  onResolved: (started?: { runId?: string | null }, settled?: GateSettled) => void;
  /**
   * The page's own moves (Archive, Next item), printed beside the stamp once
   * the gate is answered. Absent on a standalone mount, which keeps the stamp
   * alone.
   */
  afterActs?: ReactNode;
  /**
   * The quiet verbs a person reaches for when neither of the gate's own pair
   * fits (the case file's More menu: Split, Back to Proposed), on the foot's
   * left beside the label, while the gate is still open.
   */
  footMore?: ReactNode;
  /**
   * Is this reader on the Team that owns the item? (ADR-0044 edge case 22.)
   * `undefined` means the host has not resolved membership, and the card
   * behaves as it always did rather than locking a reader out on a guess.
   */
  isTeamMember?: boolean;
  /** The Team's suggested repository for this ticket, for the inline repository ask. */
  repoSuggestion?: RepoSuggestion | null;
}) {
  const { resolveGate, error } = useResolveWorkItemGate();
  // An accept the server held for a repository: what it said, what to pick
  // from, and the edit the accept carried, for the retry on an older backend.
  const [repoAsk, setRepoAsk] = useState<{
    message: string;
    repos: TeamRepo[];
    edit: { editedProposal?: Record<string, unknown> };
  } | null>(null);
  const [repoBusy, setRepoBusy] = useState(false);
  // Commissioning resolves through its own transaction route, not the gate's.
  const { commission, error: commissionError } = useCommissionProject();
  const { push, error: pushError } = usePushMilestone();
  const [feedback, setFeedback] = useState("");
  // The note box opens when a secondary that needs words is pressed with none;
  // a needs_input gate's box is the answer itself and is always open.
  const [noteOpen, setNoteOpen] = useState(false);
  // The visible baton pass: after the primary the footer flips in place to
  // its stamp, and only then tells the page (the refetch).
  const [handedOff, setHandedOff] = useState<string | null>(null);
  const [answerHint, setAnswerHint] = useState(false);
  // The stamp the ending the reader chose earns, when it has its own.
  const [stampOverride, setStampOverride] = useState<string | null>(null);
  const handoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (handoffTimer.current != null) clearTimeout(handoffTimer.current);
    },
    [],
  );

  // The act first, the kind only as the fallback for a gate no Job minted.
  const jobRead = readJobGatePayload(gate.payload);
  const heldReview = isHeldReviewGate(gate);
  const planAsk = isPlanAsk(gate);
  const actConfig = planAsk
    ? null
    : ((!heldReview && jobRead ? jobActConfig(jobRead) : null) ??
      gateVerbConfig(
        gate.kind,
        heldReview,
        resumesApprovedPlan(gate),
        isClosedPullRequestStop(gate),
      ));
  // Start plan says what follows by the Plan line's rung: an Autonomous line
  // never brings the plan back for a sign-off.
  const startsPlan = gate.kind === "accept_proposal" || jobRead?.act === "accept_proposal";
  const config =
    actConfig && startsPlan
      ? {
          ...actConfig,
          effect: acceptProposalEffect(planAuthority),
          handedOff: acceptProposalHandedOff(planAuthority),
        }
      : actConfig;
  const proposal = gateProposal(gate);
  const projectId = typeof gate.payload?.projectId === "string" ? gate.payload.projectId : null;
  const milestoneId =
    typeof gate.payload?.milestoneId === "string" ? gate.payload.milestoneId : null;

  if (!config) {
    return (
      <div className="wk-gate">
        <div className="wk-gate-lab">{GATE_LABEL.other}</div>
      </div>
    );
  }

  // The stamp the footer prints once the gate is acted on: the act's own when
  // a Job minted this, the kind's otherwise.
  const stamp =
    stampOverride ??
    (jobRead?.act ? JOB_ACT_COPY[jobRead.act]?.stamp : null) ??
    (gate.kind === "review_result" && !heldReview ? DELIVERY_DONE_STAMP : null) ??
    GATE_STAMPS[gate.kind as keyof typeof GATE_STAMPS] ??
    "RECORDED";

  const ids = { workItemId: gate.workItemId, gateId: gate.id };

  const handOff = (receipt: string, startedRunId?: string | null, stampWord?: string) => {
    setHandedOff(receipt);
    const settled = { stamp: stampWord ?? stamp, text: receipt };
    handoffTimer.current = setTimeout(
      () => onResolved(startedRunId ? { runId: startedRunId } : undefined, settled),
      HANDOFF_FLIP_MS,
    );
  };

  /**
   * Accept a proposal. A Team with several repositories and none named holds
   * it with `choose_repo`: the card then asks which, inline, rather than
   * printing the refusal.
   */
  const acceptProposal = async (edit: { editedProposal?: Record<string, unknown> }) => {
    try {
      const resolved = await resolveGate({
        ...ids,
        action: "accept_proposal",
        scopePatch: null,
        startPlanning: true,
        ...edit,
      });
      setRepoAsk(null);
      handOff(proposalHandoffCopy(resolved?.planning, planAuthority));
    } catch (err) {
      // Read off the error by shape (a `GateResolveError` carries `body`).
      const body =
        err instanceof Error && "body" in err
          ? (err.body as {
              error?: unknown;
              code?: unknown;
              message?: unknown;
              repos?: unknown;
            } | null)
          : null;
      if (refusalCode(body) !== "choose_repo") throw err;
      const said =
        typeof body?.message === "string" && body.message.trim() ? body.message.trim() : null;
      const fromError = err instanceof Error && err.message !== "choose_repo" ? err.message : null;
      setRepoAsk({
        message: said ?? fromError ?? REPO_CHOOSE_TO_START,
        repos: readRepoList(body?.repos),
        edit,
      });
    }
  };

  /**
   * The repository picked for a held accept. The PATCH starts the held
   * planning itself and says so; an older backend does not, and the accept
   * is sent again.
   */
  const pickRepoForAccept = async (repoId: string) => {
    if (!repoId || !repoAsk) return;
    setRepoBusy(true);
    try {
      const patched = await patchWorkItemRepo(gate.workItemId, repoId);
      if (!patched.ok) {
        setRepoAsk({
          ...repoAsk,
          message: patched.message,
          repos: patched.repos.length > 0 ? patched.repos : repoAsk.repos,
        });
        return;
      }
      if (patched.planning) {
        window.dispatchEvent(new CustomEvent("tickets.invalidate"));
        setRepoAsk(null);
        handOff(proposalHandoffCopy(patched.planning, planAuthority));
        return;
      }
      await acceptProposal(repoAsk.edit);
    } catch {
      // The hook's `error` state renders the message; keep the gate open.
    } finally {
      setRepoBusy(false);
    }
  };

  /**
   * A fix round's ask, answered. Keyed off the ACT, because this ask rides the
   * build item's own open gate: sending it through the kind's branch would
   * resolve the wrong decision.
   */
  const answerFixRound = async (
    choice: JobActChoiceId | undefined,
    receipt: string,
    nextStamp?: string,
  ) => {
    const note = feedback.trim();
    try {
      const done = await resolveGate({
        ...ids,
        action: "perform_job_act",
        ...(choice ? { choice } : {}),
        ...(note ? { feedback: note } : {}),
      });
      if (nextStamp) setStampOverride(nextStamp);
      handOff(done?.detail ?? receipt, null, nextStamp);
    } catch {
      // The hook's `error` state renders the message; keep the gate open.
    }
  };

  const gateAsk = gateAskOf(gate);
  // An act with nowhere to land here keeps its verb dark. The rule is shared
  // with the item rail (`gatePrimaryBlock`), so neither offers what the other
  // would not.
  const primaryBlock = gatePrimaryBlock(gate, { answered: 0, total: 0 });

  const handleResolve = async (
    intent: DecisionIntent,
    editedProposal?: Record<string, unknown> | null,
  ) => {
    // Carry the edit only on approve, only when the gate rendered a proposal.
    const edit = intent === "approve" && proposal && editedProposal ? { editedProposal } : {};
    if (jobRead?.act === "push_fix") {
      if (intent === "approve") {
        await answerFixRound(config.approveChoice, config.handedOff, config.approveStamp);
        return;
      }
      if (config.declineChoice) {
        await answerFixRound(
          config.declineChoice,
          config.declineHandedOff ?? config.handedOff,
          config.declineStamp,
        );
        return;
      }
      const note = feedback.trim();
      try {
        await resolveGate({
          ...ids,
          action: "decline_job_act",
          ...(note ? { feedback: note } : {}),
        });
        onResolved();
      } catch {
        // The hook's `error` state renders the message; keep the gate open.
      }
      return;
    }
    try {
      if (intent === "approve") {
        switch (gate.kind) {
          case "accept_proposal":
            await acceptProposal(edit);
            return;
          case "needs_input": {
            const answer = feedback.trim();
            if (!answer && !resumesApprovedPlan(gate)) {
              setAnswerHint(true);
              return;
            }
            const answered = await resolveGate({
              ...ids,
              action: "answer_input",
              response: { text: answer },
              resume: true,
              ...edit,
            });
            // An answer to a budget stop continues the stopped build.
            if (answered?.continued === "budget_raised") {
              handOff("The build is continuing from what it kept.");
              return;
            }
            // The stop card's restart keeps its own receipt; a plain question's
            // says what the server did with the answer.
            if (!resumesApprovedPlan(gate)) {
              handOff(plainAskReceipt(answered));
              return;
            }
            // Run again builds the approved plan at once; the re-offer receipt
            // is for the case the route brought the plan back for approval.
            if (answered?.phase === "executing") {
              handOff("The approved plan is building again.");
              return;
            }
            break;
          }
          case "review_result": {
            // The held review's per-finding decisions ride the SAME resolve
            // as the verdict. A delivery with no pull request is marked done.
            const findings = review?.resolutions ?? [];
            const resolved = await resolveGate({
              ...ids,
              action: "accept_result",
              ...(findings.length > 0 ? { findings } : {}),
              ...edit,
            });
            // A held review reports its real delivery: never claim "posted"
            // over a post that was skipped or failed.
            const delivery = resolved?.reviewDelivery;
            if (delivery && !delivery.posted) {
              handOff(
                delivery.reason === "pr_closed" || delivery.reason === "head_moved"
                  ? REVIEW_CONFIRM_NOT_POSTED_STALE
                  : delivery.reason === "nothing_kept"
                    ? REVIEW_CONFIRM_NOTHING_KEPT
                    : REVIEW_CONFIRM_NOT_POSTED_FAILED,
              );
              return;
            }
            if (delivery?.heldInDraft) {
              handOff(REVIEW_CONFIRM_KEPT_DRAFT);
              return;
            }
            break;
          }
          case "commission_project":
            if (!projectId) return;
            await commission({ projectId, gateId: gate.id });
            break;
          case "job_act": {
            // The route dispatches the act BEFORE resolving, so the receipt is
            // the act's own report; a failure throws and leaves the gate open.
            const done = await resolveGate({ ...ids, action: "perform_job_act" });
            handOff(done?.detail ?? config.handedOff);
            return;
          }
          default:
            // The milestone push resolves the gate and THEN pushes, in that
            // order, through its own route.
            if (gate.kind !== MILESTONE_DECOMPOSITION_GATE_KIND) return;
            if (!projectId || !milestoneId) return;
            await push({ projectId, milestoneId, gateId: gate.id });
            break;
        }
        handOff(config.handedOff);
        return;
      }

      switch (gate.kind) {
        case "accept_proposal":
          await resolveGate({ ...ids, action: "decline_proposal" });
          onResolved();
          break;
        case "review_result":
          if (!feedback.trim()) {
            setNoteOpen(true);
            return;
          }
          await resolveGate({
            ...ids,
            action: "request_result_changes",
            feedback: feedback.trim(),
          });
          onResolved();
          break;
        case "needs_input":
          // Not needed: the item closes. A plain question offers it, and so
          // does the closed-pull-request stop; no other resumable stop does.
          if (resumesApprovedPlan(gate) && !isClosedPullRequestStop(gate)) return;
          await resolveGate({ ...ids, action: "dismiss_input" });
          onResolved();
          break;
        case "job_act": {
          // The other half of an act: it ships nothing and records the
          // send-back, so a note is welcome and never demanded.
          const note = feedback.trim();
          await resolveGate({
            ...ids,
            action: "decline_job_act",
            ...(note ? { feedback: note } : {}),
          });
          onResolved();
          break;
        }
        default:
          // Every other ask has no secondary verb to press.
          return;
      }
    } catch {
      // The hook's `error` state renders the message; keep the gate open.
    }
  };

  const recordNote = gateRecordNote(gate.kind, workerName ?? null);
  // The Job's head: the Agent's line and the Authority word, then whose click
  // it is. Only off the dock, where there is room for them.
  const jobSentence = jobRead?.job?.sentence ?? null;
  const supervisedBy = supervisedByLine(gate.supervisorName);
  const clickLine = jobRead ? whoseClickLine(gate.supervisorName) : null;
  // A non-member sees the verbs dark with the reason, never an idle page.
  // Scoped to a Job's gates, the only ones the act route checks the roster on.
  const blocked = jobRead && isTeamMember === false ? GATE_NOT_A_MEMBER : null;
  // The act may have nowhere to land here; the decline stays live.
  const verbBlocked = primaryBlock === "act_elsewhere" ? jobActElsewhere(jobRead?.act) : null;
  const docked = afterActs != null;
  const noteBoxOpen = gate.kind === "needs_input" || noteOpen || feedback !== "";
  const consequence = config.effect;

  return (
    <div
      className={
        "wk-gate wk-gatefoot" +
        (handedOff ? " settle-committed" : "") +
        (gateAsk && !handedOff ? " has-ask" : "")
      }
    >
      {!handedOff && review && review.findings.length > 0 && <ReviewFindingsCards state={review} />}
      {!handedOff && jobRead?.act === "push_fix" && jobRead.evidence.kind === "patch_ready" && (
        <PatchRoundEvidence evidence={jobRead.evidence} />
      )}
      {/* Any gate can carry the ask its opener wrote; this footer draws it
          above the verbs, the same way the stopped-build card does. */}
      {!handedOff && gateAsk && (
        <div className="wk-gate-ask" data-testid="gate-ask">
          <p className="wk-ni-title">{gateAsk.headline}</p>
          <AskFacts facts={gateAsk.facts} limit={3} />
          <AskNeed ask={gateAsk} name={workerName ?? null} />
        </div>
      )}

      {!handedOff && !docked && (jobSentence || supervisedBy) && (
        <div className="wk-gate-job" data-testid="wk-gate-job">
          {jobSentence && (
            <span className="wk-gate-job-line" data-testid="wk-gate-job-sentence">
              {jobSentence}
            </span>
          )}
          {supervisedBy && (
            <span className="wk-gate-job-auth" data-testid="wk-gate-supervisor">
              {supervisedBy}
            </span>
          )}
        </div>
      )}

      {config.feedback && !handedOff && noteBoxOpen && (
        <textarea
          className="wk-gate-input"
          autoFocus={noteOpen}
          value={feedback}
          onChange={(e) => {
            setFeedback(e.target.value);
            setAnswerHint(false);
          }}
          rows={2}
          placeholder={config.feedback.placeholder}
          aria-label="Gate response"
        />
      )}

      {!handedOff && answerHint && (
        <p className="wk-gate-err" data-testid="wk-gate-answer-hint" role="alert">
          Add an answer to send.
        </p>
      )}

      {handedOff ? (
        <StampRow
          stamp={stamp}
          text={handedOff}
          by={stampByLine(resolverName ?? null, "just now")}
          acts={afterActs}
        />
      ) : (
        <DecisionCard
          proposal={proposal}
          verbLabel={config.verbLabel}
          effect={config.effect}
          {...(config.declineEffect ? { declineEffect: config.declineEffect } : {})}
          footStart={
            <>
              <FootLead label={config.label} consequence={consequence} />
              {footMore}
            </>
          }
          effectHover
          declineLabel={config.declineLabel}
          {...(config.alternative
            ? {
                alternative: {
                  label: config.alternative.label,
                  effect: config.alternative.effect,
                  testId: "wk-gate-alternative",
                  onSelect: () =>
                    answerFixRound(
                      config.alternative!.choice,
                      config.alternative!.handedOff,
                      config.alternative!.stamp,
                    ),
                },
              }
            : {})}
          blocked={blocked}
          verbBlocked={verbBlocked}
          onResolve={handleResolve}
          fieldIdPrefix={`gate-${gate.id}`}
          testIds={{
            proposal: "wk-gate-proposal",
            verb: "wk-gate-verb",
            decline: "wk-gate-decline",
            effect: "wk-gate-effect",
            declineEffect: "wk-gate-decline-effect",
            receipt: "wk-gate-receipt",
          }}
        />
      )}

      {!handedOff && !docked && clickLine && (
        <p className="wk-gate-click-note" data-testid="wk-gate-click-note">
          {clickLine}
        </p>
      )}

      {!handedOff && !docked && recordNote && (
        <p className="wk-gate-record-note" data-testid="wk-gate-record-note">
          {recordNote}
        </p>
      )}

      {repoAsk && !handedOff && (
        <div className="cf-signals-line cf-repo-line" data-testid="wk-gate-repo-ask">
          <span role="status">{repoAsk.message}</span>
          {repoAsk.repos.length > 0 && (
            <RepoSelect
              repos={repoAsk.repos}
              value=""
              disabled={repoBusy}
              label={REPO_LABEL}
              testId="wk-gate-repo"
              suggestion={repoSuggestion ?? null}
              confirmSuggestion
              onChange={(repoId) => void pickRepoForAccept(repoId)}
            />
          )}
        </div>
      )}

      {!repoAsk && (error ?? commissionError ?? pushError) && (
        <p className="wk-gate-err" role="alert">
          {(error ?? commissionError ?? pushError)!.message}
        </p>
      )}
    </div>
  );
}
